/**
 * Utilities for resolving attachment references in MARKDOWN_V1 session parts.
 *
 * BE embeds attachment links as fragment anchors (#pdf-ref-0, #img-ref-0) in markdown content,
 * with structured metadata in the `attachment_references` array. These utilities resolve the
 * fragment hrefs to actual proxied URLs at render time so they work as standard <a> links.
 */

export interface AttachmentReference {
  ref_id: string;
  url: string;
  page: number | null;
}

const ATTACHMENT_REF_PATTERN = /^#(pdf-ref-|img-ref-)/;
const MARKDOWN_ATTACHMENT_LINK_PATTERN = /\[([^\]]*)\]\(#((?:pdf|img)-ref-[^)]+)\)/g;

export function isAttachmentRefHref(href: string): boolean {
  return ATTACHMENT_REF_PATTERN.test(href);
}

export function getAttachmentRefId(href: string): string | null {
  if (!isAttachmentRefHref(href)) return null;
  return href.slice(1); // remove '#'
}

export function buildAttachmentRefMap(
  refs: AttachmentReference[] | undefined | null
): Map<string, AttachmentReference> | null {
  if (!refs?.length) return null;
  return new Map(refs.map(r => [r.ref_id, r]));
}

/**
 * Replace #pdf-ref-* and #img-ref-* fragment links in markdown with resolved proxied URLs.
 *
 * For PDF refs with a page number, appends #page=N to the resolved URL.
 * For image refs (or PDFs without a page), uses the resolved URL directly.
 */
export function resolveAttachmentRefsInMarkdown(
  content: string,
  refMap: Map<string, AttachmentReference>,
  resolveUrl: (originalUrl: string) => string | null
): string {
  return content.replace(MARKDOWN_ATTACHMENT_LINK_PATTERN, (match, linkText, refId) => {
    const ref = refMap.get(refId);
    if (!ref) return match;

    const proxiedUrl = resolveUrl(ref.url);
    if (!proxiedUrl) return match;

    const isPdf = refId.startsWith("pdf-ref-");
    let urlWithFragment: string;
    if (isPdf && ref.page != null) {
      urlWithFragment = `${proxiedUrl}#mem-pdf-page=${ref.page}`;
    } else if (isPdf) {
      urlWithFragment = `${proxiedUrl}#mem-pdf`;
    } else {
      urlWithFragment = `${proxiedUrl}#mem-img`;
    }

    return `[${linkText}](${urlWithFragment})`;
  });
}
